(ns admin.components.forms.fields.markup-editor
  (:require
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]
   ["draft-js" :as draft-js]
   ["@draft-js-plugins/drag-n-drop" :rename {default draft-js-drag-n-drop-plugin}]
   ["@draft-js-plugins/editor" :as draft-js-plugins-editor]
   ["@draft-js-plugins/focus" :rename {default draft-js-focus-plugin}]
   ["@draft-js-plugins/image" :rename {default draft-js-image-plugin}]
   ["draft-js-export-html" :as draft-js-export-html]
   ["draft-js-import-html" :as draft-js-import-html]
   [goog.object :as gobj]
   [reagent-form.utils :refer [initialize-field!
                               update-field-value!
                               get-field-errors
                               validate-field!]]

   [kyber.components.base.button :as button]
   [kyber.utils :as ku]
   [kyber.styles.core :refer [gs styles->classes]]

   [admin.constants :as ac]
   [admin.components.composite.resource-uploader :as resource-uploader]))

(def classes
  (styles->classes
   {:toolbar-container
    {:position :relative
     :border (str "1px solid " (gs [:colors :gray-scale-200]))}

    :active-editor-button
    {"& > i"
     {:font-weight (gs [:font-weight :title-10])}}

    :media-hover-container
    {:position :absolute
     :top 0
     :left 0
     :display :flex
     :justify-content :center
     :align-items :center
     :padding (gs [:spacing :p20])
     :width "100%"
     :height "100%"
     :background-color "rgba(0,0,0,0.5)"
     :border-radius (gs [:radius :r3])}}))

(def ^:private image-block-styles
  {:margin :auto
   :display :block
   :max-width "100%"
   :padding-top "20px"
   :padding-bottom "20px"})

(def ^:private editor-image-styles
  {:position :relative
   :border (str "4px dashed " (gs [:colors :gray-scale-200]))
   :border-radius (gs [:radius :r3])})

(def ^:private convert-from-html
  (.-stateFromHTML draft-js-import-html))

(def ^:private content-state->html
  (.-stateToHTML draft-js-export-html))

(def ^:private compose-decorators
  (.-composeDecorators draft-js-plugins-editor))

(def ^:private Editor
  (reagent/adapt-react-class (.-default draft-js-plugins-editor)))

(def ^:private EditorState
  (.-EditorState draft-js))

(def ^:private Modifier
  (.-Modifier draft-js))

(def ^:private RichUtils
  (.-RichUtils draft-js))

(defn- get-current-content
  [editor-state]
  (.getCurrentContent editor-state))

(defn- get-entity
  "Returns the entity of a given block"
  [block content-state]
  (when-let [block-entity (.getEntityAt block 0)]
    (.getEntity content-state block-entity)))

(defn- get-block-data
  "Returns a block data"
  [block content-state]
  (-> (get-entity block content-state)
      (.getData)
      (js->clj :keywordize-keys true)))

(defn- image-component
  [{:keys [contentState block onDragStart onClick]}]
  (let [{:keys [src]} (get-block-data block contentState)]
    [:img (merge {:style (merge image-block-styles
                                editor-image-styles)
                  :on-click onClick
                  :on-drag-start onDragStart
                  :role "presentation"
                  :src src})]))
;; Plugins

(def ^:private block-dnd-plugin
  (draft-js-drag-n-drop-plugin))

(def ^:private focus-plugin
  (draft-js-focus-plugin))

(def ^:private image-plugin
  (draft-js-image-plugin
   (clj->js {:imageComponent (reagent/reactify-component image-component)
             :decorator (compose-decorators
                         (gobj/get focus-plugin "decorator")
                         (gobj/get block-dnd-plugin "decorator"))})))

;; Draft JS Settings

(defonce inline-states
  {:underline "UNDERLINE"
   :bold "BOLD"
   :italic "ITALIC"
   :code "CODE"
   :subscript "SUB"
   :superscript "SUP"
   :image "IMG"})

(def style-map
  {(:italic inline-states)
   {:fontStyle :italic}

   (:bold inline-states)
   {:fontWeight (gs [:font-weight :title-10])}

   (:underline inline-states)
   {:textDecoration :underline}

   (:subscript inline-states)
   {:verticalAlign :sub :fontSize (gs [:font-size :caption-10])}

   (:superscript inline-states)
   {:verticalAlign :super :fontSize (gs [:font-size :caption-10])}})

(defn- toggle-block-style
  [{:keys [editor-state editor-node is-uploading-image]} style e]
  (.preventDefault e)
  ;; NOTE: Focus must happen before state update

  (reset! is-uploading-image false)

  (when (some? editor-node)
    (.focus editor-node))
  (reset! editor-state (.toggleBlockType RichUtils
                                         @editor-state
                                         (name style))))

(defn- toggle-inline-style
  "Updates an editor states inline style"
  [{:keys [editor-state editor-node is-uploading-image]} style e]
  (.preventDefault e)
  ;; NOTE: Focus must happen before state update

  (reset! is-uploading-image false)

  (when (some? editor-node)
    (js-invoke editor-node "focus"))
  (reset! editor-state (.toggleInlineStyle RichUtils
                                           @editor-state
                                           (style inline-states))))

(defn- add-image
  [{:keys [editor-state is-uploading-image editor-node]}
   {:keys [viewer-path]}]
  (let [new-state (js-invoke image-plugin "addImage" @editor-state viewer-path)]
    (reset! editor-state new-state)
    (reset! is-uploading-image false)
    ;; Note: Fixes issue where admins add an image an immediately save and close
    ;;       the form. This prevents the image from being saved to editor state.
    ;;       onChange event is not fired by add image plugin.
    (js/setTimeout #(js-invoke editor-node "blur") 200)))

(defn- inline-style-enabled?
  [editor-node current-inline-styles style-key]
  (and (some? editor-node)
       (.has current-inline-styles (style-key inline-states))))

(defn- content-block-enabled?
  [editor-node content-block-type target-content-block-type]
  (and (some? editor-node)
       (= content-block-type target-content-block-type)))

(defn- render-markup-media-uploader
  [{:keys [on-success activity-uuid]}]
  (let [file-is-hovering (reagent/atom false)
        upload-state (reagent/atom (merge ac/initial-uploader-state
                                          {:public-access true
                                           :on-success on-success}))]
    (fn [{:keys [accepted-file-types]}]
      (let [{:keys [is-uploading]} @upload-state]
        [resource-uploader/render-dropzone
         {:file-is-hovering
          file-is-hovering

          :component
          (cond
            (true? is-uploading)
            [resource-uploader/render-uploading-in-progress @upload-state]

            :else
            [resource-uploader/render-file-selection
             {:file-is-hovering
              file-is-hovering

              :accepted-file-types
              accepted-file-types

              :on-file-selection
              (fn [file]
                (re-frame/dispatch
                 [:resources/get-activity-file-upload-key
                  {:activity-uuid activity-uuid
                   :upload-state upload-state}])

                (swap! upload-state
                       merge
                       {:is-uploading true
                        :original-file file
                        :percent-uploaded 0}))}])}]))))

(defn- render-markup-toolbar
  [_]
  (let [is-uploading-image (reagent/atom false)]
    (fn [{:keys [editor-state
                 editor-node
                 content-block-type
                 supports-images
                 activity-uuid]}]
      (let [current-inline-styles
            (.getCurrentInlineStyle @editor-state)

            bold-enabled
            (inline-style-enabled? editor-node
                                   current-inline-styles
                                   :bold)

            italic-enabled
            (inline-style-enabled? editor-node
                                   current-inline-styles
                                   :italic)

            underline-enabled
            (inline-style-enabled? editor-node
                                   current-inline-styles
                                   :underline)

            is-header-one
            (content-block-enabled? editor-node
                                    content-block-type
                                    :header-one)

            is-header-two
            (content-block-enabled? editor-node
                                    content-block-type
                                    :header-two)

            is-header-three
            (content-block-enabled? editor-node
                                    content-block-type
                                    :header-three)

            is-unordered-list
            (content-block-enabled? editor-node
                                    content-block-type
                                    :unordered-list-item)

            is-ordered-list
            (content-block-enabled? editor-node
                                    content-block-type
                                    :ordered-list-item)

            is-subscript
            (inline-style-enabled? editor-node
                                   current-inline-styles
                                   :subscript)

            is-superscript
            (inline-style-enabled? editor-node
                                   current-inline-styles
                                   :superscript)

            editor-context
            {:editor-state editor-state
             :editor-node editor-node
             :is-uploading-image is-uploading-image}

            toggle-block-style*
            (partial toggle-block-style editor-context)

            toggle-inline-style*
            (partial toggle-inline-style editor-context)]
        [:div
         [:div {:class (:toolbar-container classes)}
          [button/icon-button
           (cond-> {:on-click (partial toggle-block-style* :header-one)}
             is-header-one
             (assoc :class (:active-editor-button classes)))
           (if is-header-one :heading-1 :heading-1-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-block-style* :header-two)}
             is-header-two
             (assoc :class (:active-editor-button classes)))
           (if is-header-two :heading-2 :heading-2-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-block-style* :header-three)}
             is-header-three
             (assoc :class (:active-editor-button classes)))
           (if is-header-three :heading-3 :heading-3-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-inline-style* :bold)}
             bold-enabled
             (assoc :class (:active-editor-button classes)))
           (if bold-enabled :bold :bold-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-inline-style* :italic)}
             italic-enabled
             (assoc :class (:active-editor-button classes)))
           (if italic-enabled :italic :italic-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-inline-style* :underline)}
             underline-enabled
             (assoc :class (:active-editor-button classes)))
           (if underline-enabled :underline :underline-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-block-style*
                                       :unordered-list-item)}
             is-unordered-list
             (assoc :class (:active-editor-button classes)))
           (if is-unordered-list :list :list-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-block-style* :ordered-list-item)}
             is-ordered-list
             (assoc :class (:active-editor-button classes)))
           (if is-ordered-list :list-ordered :list-ordered-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-inline-style*
                                       :subscript)}
             is-subscript
             (assoc :class (:active-editor-button classes)))
           (if is-subscript :subscript :subscript-light)]

          [button/icon-button
           (cond-> {:on-click (partial toggle-inline-style*
                                       :superscript)}
             is-superscript
             (assoc :class (:active-editor-button classes)))
           (if is-superscript :superscript :superscript-light)]

          (when (true? supports-images)
            [button/icon-button
             (cond-> {:on-click #(do
                                   (.preventDefault %)
                                   (swap! is-uploading-image not))}
               @is-uploading-image
               (assoc :class (:active-editor-button classes)))
             :images])]
         (when (true? @is-uploading-image)
           [render-markup-media-uploader
            {:accepted-file-types #{:image}
             :activity-uuid activity-uuid
             :on-success (partial add-image editor-context)}])]))))

(defn- enhance-block
  [content-state block]
  (let [entity-key (.getEntityAt block 0)]
    (if (some? entity-key)
      (let [entity-block (.getEntity content-state entity-key)
            entity-type (.getType entity-block)]
        (case entity-type
          "IMAGE" (.merge block #js {:type "atomic"})
          block))
      block)))

(defn- enhance-content-state
  "Properly tags unstyled elements with their proper type (i.e. img = atomic)"
  [content-state]
  (let [current-block-map (.getBlockMap content-state)
        updated-block-map (.map current-block-map
                                (partial enhance-block content-state))]
    (.set content-state "blockMap" updated-block-map)))

(def empty-editor-state "<p><br></p>")

(defn- is-empty?
  [content]
  (= empty-editor-state content))

(defn- remove-empty-values
  [value]
  (when-not (is-empty? value) value))

(def ^{:private true} required-validator
  {:validator #(not (or (nil? %)
                        (is-empty? %)))
   :message "Required"})

(defn- on-editor-change
  [on-change editor-state updated-editor-state]
  (reset! editor-state updated-editor-state)
  (on-change))

(defn- custom-inline-fn [content-block options]
  (let [tag-name (gobj/get content-block "tagName")
        Entity (gobj/get options "Entity")
        Style (gobj/get options "Style")]

    (condp = tag-name
      (inline-states :subscript)
      (Style (:subscript inline-states))

      (inline-states :superscript)
      (Style (:superscript inline-states))

      (inline-states :image)
      (Entity "IMAGE"
              #js {:src (js-invoke content-block "getAttribute" "src")})
      nil)))

(defn- custom-entity-style-fn
  [entity]
  (let [entity-type (.get entity "type")]
    (case entity-type
      "IMAGE"
      (let [data (.getData entity)]
        (clj->js
         {:element "img"
          :attributes {:src (.-src data)}
          :style image-block-styles}))
      nil)))

(defn- custom-inline-style-fn [styles _constructors]
  (let [tag (first (.toArray styles))]
    (condp = tag
      (inline-states :superscript) #js {:element "sup"}
      (inline-states :subscript) #js {:element "sub"}
      ;; TODO Look into return type
      styles)))

(defn- html->editor-state
  [html]
  (let [content-state (convert-from-html html #js {:customInlineFn
                                                   custom-inline-fn})
        enhanced-content-state (enhance-content-state content-state)]
    (.createWithContent EditorState enhanced-content-state)))

(defn- handlePastedText [on-change editor-state text html _editor-state]
  (when (nil? html)
    (let [current-content (get-current-content @editor-state)
          current-selection (.getSelection @editor-state)
          new-content (.replaceText Modifier current-content current-selection (.trim text))
          new-state (.push EditorState @editor-state new-content "insert-characters")]
      (on-editor-change on-change editor-state new-state)
      "handled")))   

(defn render
  [{:keys [node form-state]}]
  (let [params
        (second node)

        {:keys [default-errors
                field-key
                default-value
                required
                supports-images
                activity-uuid]
         :or {required false
              supports-images false}}
        (:rf/custom-field params)

        editor-state
        (reagent/atom (or (if (and (some? default-value)
                                   (false? (is-empty? default-value)))
                            (html->editor-state default-value)
                            (.createEmpty EditorState))
                          (.createEmpty EditorState)))

        !ref
        (atom nil)

        on-change
        (ku/debounce
         (fn []
           (update-field-value!
            form-state
            field-key
            (-> @editor-state
                get-current-content
                (content-state->html
                 (clj->js {:entityStyleFn custom-entity-style-fn
                           :inlineStyleFn custom-inline-style-fn}))))
           (when (seq (get-field-errors @form-state field-key))
             (validate-field! form-state field-key)))
         200)]

    (initialize-field! form-state
                       field-key
                       {:default-errors (or default-errors [])
                        :default-hints []
                        :default-value (or default-value nil)
                        :hint-triggers []
                        :masks []
                        :transformers [remove-empty-values]
                        :validators (cond-> []
                                      (true? required)
                                      (conj required-validator))})

    (fn []
      (when @editor-state
        (let [start-key (-> @editor-state
                            (.getSelection)
                            (.getStartKey))
              content-block-type (-> @editor-state
                                     get-current-content
                                     (.getBlockForKey start-key)
                                     (.getType)
                                     (keyword))]
          [:div.markup-editor.full-size-editor
           [render-markup-toolbar {:editor-state editor-state
                                   :editor-node @!ref
                                   :content-block-type content-block-type
                                   :supports-images supports-images
                                   :activity-uuid activity-uuid}]

           [Editor
            {:editorState @editor-state
             :customStyleMap (clj->js style-map)
             :handlePastedText (partial handlePastedText on-change editor-state)
             :onChange (partial on-editor-change on-change editor-state)
             :plugins (clj->js [block-dnd-plugin
                                focus-plugin
                                image-plugin])
             :ref #(reset! !ref %)}]])))))

